/**
 * Visibility Diagnosis Service
 *
 * API client for AI & Search Engine Visibility Diagnosis endpoints.
 *
 * Part of Story 63: AI & Search Engine Visibility Diagnosis
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface VisibilityDiagnosis {
  site_id: string;
  domain?: string;
  session_behavior: Record<string, BotSessionBehavior>;
  robots_analysis: RobotsAnalysis;
  structured_data?: StructuredDataAnalysis;
  entry_points: BotEntryPoints[];
  depth_dropoff: BotDepthDropoff[];
  bot_coverage: BotCoverage;
  content_context: Record<string, ContentContext>;
  health_scores: Record<string, BotHealthScore>;
  recommendations: Recommendation[];
  critical_issues: CriticalIssue[];
  analysis_duration_ms: number;
  analyzed_at?: string;
  updated_at?: string;
}

export interface StructuredDataAnalysis {
  status: 'success' | 'error';
  url?: string;
  error?: string;
  schemas: SchemaItem[];
  raw_types: string[];
  has_organization: boolean;
  has_website: boolean;
  has_search_action: boolean;
  has_breadcrumb: boolean;
  has_product?: boolean;
  has_faq?: boolean;
  schema_count?: number;
}

export interface SchemaItem {
  type: string;
  name?: string | null;
  url?: string | null;
  hasRating?: boolean;
  hasContactPoint?: boolean;
  hasPotentialAction?: boolean;
}

export interface BotSessionBehavior {
  bot_name: string;
  display_name: string;
  total_sessions: number;
  total_requests: number;
  avg_urls_per_session: number;
  median_urls_per_session: number;
  avg_duration_min: number;
  max_urls_per_session: number;
  is_ai_bot: boolean;
  diagnosis: {
    severity: 'critical' | 'warning' | 'info' | 'healthy';
    message: string;
  };
}

export interface RobotsAnalysis {
  url: string;
  status: 'found' | 'not_found' | 'error';
  content_length?: number;
  error?: string;
  rules: RobotsRule[];
  ai_bot_rules: AIBotRule[];
  parameter_blocks: ParameterBlock[];
  sitemaps: string[];
}

export interface RobotsRule {
  user_agent: string;
  type: 'allow' | 'disallow';
  pattern: string;
  affects_ai_bots: boolean;
}

export interface AIBotRule {
  user_agent: string;
  type: 'allow' | 'disallow';
  pattern: string;
  impact: 'direct' | 'high' | 'medium';
  description: string;
}

export interface ParameterBlock {
  rule: string;
  description: string;
  impact: string;
}

export interface BotEntryPoints {
  bot_name: string;
  display_name: string;
  total_sessions: number;
  entry_points: {
    url_path: string;
    page_type: string;
    url_depth: number;
    session_count: number;
    pct_of_sessions: number;
  }[];
  patterns: {
    homepage_pct: number;
    product_pct: number;
    category_pct: number;
    other_pct: number;
    avg_entry_depth: number;
  };
}

export interface BotDepthDropoff {
  bot_name: string;
  display_name: string;
  max_depth: number;
  total_urls: number;
  total_visits: number;
  depth_distribution: {
    depth: number;
    unique_urls: number;
    total_visits: number;
    pct_of_urls: number;
    pct_of_visits: number;
  }[];
  dropoff_point?: {
    depth: number;
    drop_pct: number;
    visits_before: number;
    visits_after: number;
  };
  insight: string;
}

export interface BotCoverage {
  search_bots: BotCoverageItem[];
  ai_bots: BotCoverageItem[];
  other_bots: BotCoverageItem[];
  total_urls: number;
  aeo_total: number;
}

export interface BotCoverageItem {
  name: string;
  display_name: string;
  urls_crawled: number;
  total_visits: number;
  max_depth: number;
  avg_depth: number;
  coverage_pct: number;
  aeo_urls_crawled: number;
  aeo_coverage_pct: number;
  is_ai_bot: boolean;
  status: 'healthy' | 'warning' | 'critical';
}

export interface ContentContext {
  bot_name: string;
  total_visits: number;
  homepage_visits: number;
  about_visits: number;
  product_visits: number;
  category_visits: number;
  brand_context_score: number;
  diagnosis: {
    severity: 'critical' | 'warning' | 'healthy';
    message: string;
  };
}

export interface BotHealthScore {
  bot_name: string;
  display_name: string;
  is_ai_bot: boolean;
  total_score: number;
  max_score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  breakdown: {
    session_depth: { score: number; max: number; value: number };
    coverage: { score: number; max: number; value: number };
    entry_diversity: { score: number; max: number; value: number };
    blocking_rules: { score: number; max: number; blocked: boolean };
    content_context: { score: number; max: number; value: number };
  };
}

export interface Recommendation {
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  title: string;
  description: string;
  implementation?: string;
  impact: string;
}

export interface CriticalIssue {
  severity: 'critical' | 'high' | 'warning';
  category: string;
  bot?: string;
  message: string;
  impact?: string;
  rule?: string;
}

// API Functions
export async function getVisibilityDiagnosis(siteId: string): Promise<VisibilityDiagnosis | null> {
  const response = await authFetch(`/api/crawl-intelligence/visibility-diagnosis?siteId=${siteId}`);
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch visibility diagnosis');
  }
  const data = await response.json();
  return data.success ? data.data : null;
}

export async function runVisibilityDiagnosis(siteId: string): Promise<VisibilityDiagnosis> {
  const response = await authFetch('/api/crawl-intelligence/visibility-diagnosis/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to run visibility diagnosis');
  }
  const data = await response.json();
  return data.data;
}

export async function getHealthScores(siteId: string): Promise<Record<string, BotHealthScore>> {
  const response = await authFetch(`/api/crawl-intelligence/visibility-diagnosis/health-scores?siteId=${siteId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch health scores');
  }
  const data = await response.json();
  return data.success ? data.data : {};
}

export async function getRecommendations(siteId: string): Promise<Recommendation[]> {
  const response = await authFetch(`/api/crawl-intelligence/visibility-diagnosis/recommendations?siteId=${siteId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch recommendations');
  }
  const data = await response.json();
  return data.success ? data.data : [];
}

export async function getRobotsAnalysis(siteId: string): Promise<RobotsAnalysis | null> {
  const response = await authFetch(`/api/crawl-intelligence/visibility-diagnosis/robots-analysis?siteId=${siteId}`);
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch robots analysis');
  }
  const data = await response.json();
  return data.success ? data.data : null;
}
